/**
 * Proposal Section Renderer
 *
 * Smart component that renders proposal sections.
 * - If content is structured JSON, uses dedicated renderers
 * - If content is raw text (legacy), falls back to markdown parsing
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PreviewComponents } from '../shared/MarkdownComponents';
import {
  CoverLetterRenderer,
  ExecutiveSummaryRenderer,
  TechnicalApproachRenderer,
  ManagementApproachRenderer,
  PastPerformanceRenderer,
  PersonnelRenderer,
  PricingRenderer,
  ComplianceRenderer,
} from './SectionRenderers';
import {
  CoverLetterContent,
  ExecutiveSummaryContent,
  TechnicalApproachContent,
  ManagementApproachContent,
  PastPerformanceContent,
  PersonnelContent,
  PricingContent,
  ComplianceContent,
  isCoverLetterContent,
  isExecutiveSummaryContent,
  isTechnicalApproachContent,
  isManagementApproachContent,
  isPastPerformanceContent,
  isPersonnelContent,
  isPricingContent,
  isComplianceContent,
} from '../../types/proposal-sections';
import { smartNormalize, formatCoverLetter } from '../../lib/markdown-utils';

// ============================================================================
// TYPES
// ============================================================================

interface ProposalSectionRendererProps {
  sectionType: string;
  content: string | object;
  showCitations?: boolean;
  /** Force the pricing view (used by exports to lock the Federal Compliance view). */
  pricingView?: 'friendly' | 'federal';
  /** Hide the pricing view-mode toggle (used by exports/print). */
  pricingHideToggle?: boolean;
}

// ============================================================================
// CONTENT PARSER
// ============================================================================

interface ParsedContent {
  isStructured: boolean;
  structuredData?: any;
  rawText?: string;
}

function parseContent(content: string | object): ParsedContent {
  // If already an object, it's structured
  if (typeof content === 'object' && content !== null) {
    return { isStructured: true, structuredData: content };
  }

  // If string, try to parse as JSON
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      // Check if it looks like structured content (has expected fields)
      if (typeof parsed === 'object' && parsed !== null) {
        // Check for common structural indicators
        if (
          // Cover Letter
          parsed.companyInfo ||
          // Executive Summary
          (parsed.valueProposition && parsed.keyDiscriminators) ||
          // Technical Approach
          parsed.methodology ||
          parsed.scopeOverview ||
          parsed.toolsAndTechnologies ||
          // Management Approach
          parsed.keyRoles ||
          parsed.communicationPlan ||
          parsed.riskManagement ||
          // Past Performance
          parsed.contracts ||
          // Personnel
          parsed.keyPersonnel ||
          // Pricing
          parsed.laborCategories ||
          parsed.costBreakdown ||
          // Compliance
          parsed.farCompliance
        ) {
          return { isStructured: true, structuredData: parsed };
        }
      }
    } catch {
      // Not valid JSON, treat as raw text
    }

    // It's raw text
    return { isStructured: false, rawText: content };
  }

  // Fallback
  return { isStructured: false, rawText: String(content) };
}

// ============================================================================
// SECTION RENDERER
// ============================================================================

export function ProposalSectionRenderer({
  sectionType,
  content,
  showCitations = true,
  pricingView,
  pricingHideToggle,
}: ProposalSectionRendererProps) {
  const parsed = useMemo(() => parseContent(content), [content]);

  // Render structured content
  if (parsed.isStructured && parsed.structuredData) {
    return (
      <StructuredSectionRenderer
        sectionType={sectionType}
        data={parsed.structuredData}
        showCitations={showCitations}
        pricingView={pricingView}
        pricingHideToggle={pricingHideToggle}
      />
    );
  }

  // Render raw text with markdown
  return (
    <LegacyMarkdownRenderer
      sectionType={sectionType}
      content={parsed.rawText || ''}
      showCitations={showCitations}
    />
  );
}

// ============================================================================
// STRUCTURED SECTION RENDERER
// ============================================================================

interface StructuredSectionRendererProps {
  sectionType: string;
  data: any;
  showCitations: boolean;
  pricingView?: 'friendly' | 'federal';
  pricingHideToggle?: boolean;
}

function StructuredSectionRenderer({
  sectionType,
  data,
  showCitations,
  pricingView,
  pricingHideToggle,
}: StructuredSectionRendererProps) {
  switch (sectionType) {
    case 'coverLetter':
    case 'cover_letter':
      if (isCoverLetterContent(data)) {
        return <CoverLetterRenderer content={data} showCitations={showCitations} />;
      }
      break;

    case 'executiveSummary':
    case 'executive_summary':
      if (isExecutiveSummaryContent(data)) {
        return <ExecutiveSummaryRenderer content={data} showCitations={showCitations} />;
      }
      break;

    case 'technical':
    case 'technical_approach':
    case 'technicalApproach':
      if (isTechnicalApproachContent(data)) {
        return <TechnicalApproachRenderer content={data} showCitations={showCitations} />;
      }
      break;

    case 'management':
    case 'management_approach':
    case 'managementApproach':
      if (isManagementApproachContent(data)) {
        return <ManagementApproachRenderer content={data} showCitations={showCitations} />;
      }
      break;

    case 'pastPerformance':
    case 'past_performance':
      if (isPastPerformanceContent(data)) {
        return <PastPerformanceRenderer content={data} showCitations={showCitations} />;
      }
      break;

    case 'personnel':
    case 'key_personnel':
    case 'keyPersonnel':
      if (isPersonnelContent(data)) {
        return <PersonnelRenderer content={data} showCitations={showCitations} />;
      }
      break;

    case 'pricingStrategy':
    case 'pricing':
    case 'pricing_&_cost_strategy':
      if (isPricingContent(data)) {
        return <PricingRenderer content={data} showCitations={showCitations} defaultView={pricingView} hideToggle={pricingHideToggle} />;
      }
      break;

    case 'compliance':
    case 'far/dfars_compliance':
      if (isComplianceContent(data)) {
        return <ComplianceRenderer content={data} showCitations={showCitations} />;
      }
      break;
  }

  // Try to auto-detect based on content structure if sectionType doesn't match
  if (isTechnicalApproachContent(data)) {
    return <TechnicalApproachRenderer content={data} showCitations={showCitations} />;
  }
  if (isManagementApproachContent(data)) {
    return <ManagementApproachRenderer content={data} showCitations={showCitations} />;
  }
  if (isCoverLetterContent(data)) {
    return <CoverLetterRenderer content={data} showCitations={showCitations} />;
  }
  if (isExecutiveSummaryContent(data)) {
    return <ExecutiveSummaryRenderer content={data} showCitations={showCitations} />;
  }
  if (isPastPerformanceContent(data)) {
    return <PastPerformanceRenderer content={data} showCitations={showCitations} />;
  }
  if (isPersonnelContent(data)) {
    return <PersonnelRenderer content={data} showCitations={showCitations} />;
  }
  if (isPricingContent(data)) {
    return <PricingRenderer content={data} showCitations={showCitations} defaultView={pricingView} hideToggle={pricingHideToggle} />;
  }
  if (isComplianceContent(data)) {
    return <ComplianceRenderer content={data} showCitations={showCitations} />;
  }

  // Fallback: render data as formatted JSON (for debugging or unhandled types)
  return (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <pre className="text-sm bg-bw-surface-subtle dark:bg-gray-800 p-4 rounded-lg overflow-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

// ============================================================================
// LEGACY MARKDOWN RENDERER (for raw text content)
// ============================================================================

interface LegacyMarkdownRendererProps {
  sectionType: string;
  content: string;
  showCitations: boolean;
}

function LegacyMarkdownRenderer({
  sectionType,
  content,
  showCitations,
}: LegacyMarkdownRendererProps) {
  // Process content
  let processedContent = content;

  // Remove citations if not showing
  if (!showCitations) {
    processedContent = processedContent.replace(/\s*\[[A-Z]+-\d+(?:,\s*[^\]]+)?\]\s*/gi, ' ');
  }

  // Apply appropriate normalization based on section type
  switch (sectionType) {
    case 'coverLetter':
    case 'cover_letter':
      processedContent = formatCoverLetter(processedContent);
      break;
    default:
      processedContent = smartNormalize(processedContent);
  }

  return (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={PreviewComponents}>
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export default ProposalSectionRenderer;
